//FeatureLayerLabels.ts ==============================================================
import "../Util/Prototypes";

import * as Custom from "../Custom";
import * as MainMap from "../MainMap";
import * as Settings from "../Settings";
import * as Util from "./Util";
import * as Util_App from "./Util_App";

//ESRI javascript API.
import Color from "@arcgis/core/Color";
import FeatureLayer from "@arcgis/core/layers/FeatureLayer";
import Field from "@arcgis/core/layers/support/Field";
import Graphic from "@arcgis/core/Graphic";
import GraphicsLayer from "@arcgis/core/layers/GraphicsLayer";
import LabelClass from "@arcgis/core/layers/support/LabelClass";
import Point from "@arcgis/core/geometry/Point";
import Polygon from "@arcgis/core/geometry/Polygon";
import * as reactiveUtils from "@arcgis/core/core/reactiveUtils";
import SimpleRenderer from "@arcgis/core/renderers/SimpleRenderer";
import SimpleLineSymbol from "@arcgis/core/symbols/SimpleLineSymbol";
import SimpleMarkerSymbol from "@arcgis/core/symbols/SimpleMarkerSymbol";

const debug: boolean = false;

let midPoint: __esri.Point; //Midpoint of line segment to be labeled or for graphic text.
let segmentAngle: number; //Angle of line segment for graphic text.
let overallMapScaleChanged: boolean = false;

let busy: boolean = false;
let ii: number; //Index into the label settings array.

//Initialize each time map view is created.
export function init() {
   if (!Settings.enableDynamicLabels) {
      return; //Quit if not using dynamic labels.
   }

   Util.logMsg("Initializing Feature Layer Labels");

   //Load the geometry engine.
   import("@arcgis/core/geometry/geometryEngine").then((geomEngine) => {
      Util_App.setGeometryEngine(geomEngine);
   }); //end import geometry engine.

   let prevExtentMinX: number = 0;
   let prevCenPnt: __esri.Point = null;

   //Remove secured layers from label settings.
   let layersToRemove: string[] = [];
   let found: boolean;

   for (const labelSetting of Settings.labelLayers) {
      found = false;
      for (const operMap of Settings.operMaps) {
         if (operMap.id.toLowerCase() === labelSetting.layerName.toLowerCase()) {
            found = true;
            break;
         }
      }
      if (!found) {
         layersToRemove.Add(labelSetting);
      }
   }

   for (const layerToRemove of layersToRemove) {
      Settings.labelLayers.Remove(layerToRemove);
   }

   //Initialize label settings.
   initLabelSettings();

   //Process in reverse order so top layer settings are on top of map.
   for (let i: number = Settings.labelLayers.length - 1; i >= 0; i--) {
      const labelSetting = Settings.labelLayers[i];

      if (labelSetting.geometryType) {
         //Initialize label settings.
         labelSetting.objectIDs = [];    // Used to keep track of existing labels.
         labelSetting.seqID = {};        // Dictionary used to save sequential id used in apply edits.
         labelSetting.prevText = {};     // Dictionary used to check label visibility.
         labelSetting.prevSize = {};     // Dictionary used to update label location.
         labelSetting.prevNumStack = {}; // Dictionary used to keep track of number of stacked labels per feature.

         //Build empty feature layers for labeling.
         if (labelSetting.geometryType === "point") {
            if (labelSetting.pointGraphicMode) {
               createGraphicsLayerForTextPoints(labelSetting);
            } else {
               createLabelClassForPoints(labelSetting);
            }

         } else if (labelSetting.geometryType === "polyline") {
            createLineFeatureLayer(labelSetting);

            if (labelSetting.renderAsGraphic) {
               createGraphicsLayerForTextPoints(labelSetting);
            }

         } else if (labelSetting.geometryType === "polygon") {
            createPointFeatureLayer(labelSetting);

            if (labelSetting.renderAsGraphic) {
               createGraphicsLayerForTextPoints(labelSetting);
            }
         }
      }
   }

   //Map scale changed.
   MainMap.mapView.watch("scale", function (newScale, oldScale) {
      if (!MainMap.reBuildMapInProgress) {
         overallMapScaleChanged = true;

         //=====================================
         //Clear all graphics if zooming out.
         if (newScale > oldScale) {
            Util.logMsg("Clearing all graphics for zoom out.");

            for (const labelSetting of Settings.labelLayers) {
               if (labelSetting.renderAsGraphic) {

                  if (labelSetting.pointGraphicMode) {
                     const featureLayer = Util_App.getOperationalLayerById(labelSetting.layerName) as __esri.FeatureLayer;

                     let labelsLayerName: string = labelSetting.labelsLayerName;
                     if (featureLayer.geometryType !== "point") {
                        labelsLayerName += "_FL";
                     }

                     const graphicLayer = Util_App.getOperationalLayerById(labelsLayerName) as __esri.GraphicsLayer;
                     graphicLayer.removeAll();
                     labelSetting.objectIDs = [];

                  } else {
                     labelSetting.objectIDs = [];
                     labelSetting.seqID = {};
                     labelSetting.prevSize = {};
                     labelSetting.prevText = {};
                     labelSetting.prevNumStack = {};

                     const pointGraphicLayer = Util_App.getOperationalLayerById(labelSetting.labelsLayerName) as __esri.GraphicsLayer;
                     if (pointGraphicLayer) {
                        pointGraphicLayer.removeAll();
                     }
                  }
               }
            }
            Util.logMsg("Done clearing all graphics for zoom out.");
         }
         //=====================================

         for (const labelSetting of Settings.labelLayers) {
            if (labelSetting.geometryType) {
               labelSetting.mapScaleChanged = true;
            }
         }
         //Util.errMsg("Map scale changed: " + MainMap.mapView.scale);
      }
   });

   //Refresh labels when user is panning.
   MainMap.mapView.watch("extent", function () {
      if (!MainMap.reBuildMapInProgress) {
         if (!overallMapScaleChanged && prevCenPnt) {
            const panDist: number = MainMap.activeView.center.distance(prevCenPnt);
            const llPnt = new Point({ x: MainMap.activeView.extent.xmin, y: MainMap.activeView.extent.ymin, spatialReference: MainMap.activeView.spatialReference });
            const urPnt = new Point({ x: MainMap.activeView.extent.xmax, y: MainMap.activeView.extent.ymax, spatialReference: MainMap.activeView.spatialReference });
            const diagDist: number = llPnt.distance(urPnt);
            const percentMoved: number = panDist / diagDist * 100;

            if (percentMoved > 5) {
               Util.logMsg("Extent changed 5%. Start process labels.");
               prevCenPnt = MainMap.activeView.center;
               startProcessLabels();
            }
         }
      }
   });

   //Refresh labels when map is stationary.
   reactiveUtils?.when(() => MainMap.activeView?.stationary === true, function () {
      overallMapScaleChanged = false;

      if (MainMap.activeView.extent.xmin !== prevExtentMinX) {
         if (!MainMap.reBuildMapInProgress) {
            Util.logMsg("Stationary map. Start process labels.");
            prevCenPnt = MainMap.activeView.center;
            //GraLay.temp.removeAll(); //Uncomment when showing line segment graphics.
            startProcessLabels(true);
         }
      }
      prevExtentMinX = MainMap.activeView.extent.xmin;
   });
   Util.logMsg("Done initializing Feature Layer Labels");

} //end Initialize.

//Start the labeling process.
export function startProcessLabels(forceRefresh?: boolean) {
   if (!Settings.enableDynamicLabels) {
      return; //Quit if not using dynamic labels.
   }

   if (!forceRefresh && busy) {
      return; //Quit if busy.
   }
   busy = true;
   ii = -1;
   processLabels();
}

//Initialize one time only.
function initLabelSettings() {
   Util.logMsg("Initializing Feature Layer Labels");
   let i: number;
   let j: number;
   let caseField: string;

   //Process in reverse order so top layer settings are on top of map.
   for (i = Settings.labelLayers.length - 1; i >= 0; i--) {
      const labelSetting = Settings.labelLayers[i];

      //Get the feature layer.
      const featureLayer = Util_App.getOperationalLayerById(labelSetting.layerName) as __esri.FeatureLayer;
      if (featureLayer) {
         labelSetting.geometryType = featureLayer.geometryType; //Save geometry type.

      } else {
         labelSetting.geometryType = null;
         if (MainMap.view2D) {
            Util.errMsg("Feature layer not found for label setting: " + labelSetting.layerName);
         }
         continue;
      }

      //Labels layer name.
      if (!labelSetting.labelsLayerName) {
         labelSetting.labelsLayerName = labelSetting.layerName + " Labels";
      }

      //Convert field names to an array.
      if (labelSetting.fieldNames) {
         if (!Array.isArray(labelSetting.fieldNames)) {
            labelSetting.fieldNames = [labelSetting.fieldNames];
         }
      } else {
         labelSetting.fieldNames = [];
      }

      //Check if we are to use point graphics with an angle instead of labeling a feature layer.
      labelSetting.pointGraphicMode = false;

      if (typeof labelSetting.angleField !== "undefined" || labelSetting.renderAsGraphic) {
         if (featureLayer.geometryType === "point") {
            labelSetting.pointGraphicMode = true;
         }
      }

      //Convert custom visibility field names to an array.
      if (labelSetting.visibilityFieldNames) {
         if (!Array.isArray(labelSetting.visibilityFieldNames)) {
            labelSetting.visibilityFieldNames = [labelSetting.visibilityFieldNames];
         }
      } else {
         labelSetting.visibilityFieldNames = [];
      }

      //Set line being labeled above and below.
      let num: number = 0;
      labelSetting.aboveAndBelow = false;

      for (const s of Settings.labelLayers) {
         if (labelSetting.geometryType) {
            if (s.layerName === labelSetting.layerName) {
               num++;
            }
         }
      }

      if (num > 1) {
         labelSetting.aboveAndBelow = true;
      }

      //Max scale(s).
      if (labelSetting.maxScales) {
         if (!Array.isArray(labelSetting.maxScales)) {
            labelSetting.maxScales = [labelSetting.maxScales];
         }
      } else {
         labelSetting.maxScales = [0];
      }

      let scale: number;
      let scales: number[] = [];
      labelSetting.maxScale = 999999999;

      for (j = 0; j < labelSetting.fieldNames.length; j++) {
         scale = 0;

         if (labelSetting.maxScales[j]) {
            scale = labelSetting.maxScales[j];
            if (featureLayer.maxScale > 0 && featureLayer.maxScale > scale) {
               scale = featureLayer.maxScale;
            }
         }
         scales.Add(scale);
         if (scale < labelSetting.maxScale) {
            labelSetting.maxScale = scale;
         }
      }

      labelSetting.maxScales = scales.slice(0);  //Clone array.

      //Min scale(s).
      if (labelSetting.minScales) {
         if (!Array.isArray(labelSetting.minScales)) {
            labelSetting.minScales = [labelSetting.minScales];
         }
      } else {
         labelSetting.minScales = [999999999];
      }

      scales = [];
      labelSetting.minScale = 0;

      for (j = 0; j < labelSetting.fieldNames.length; j++) {
         scale = 999999999;

         if (labelSetting.minScales[j]) {
            scale = labelSetting.minScales[j];
            if (featureLayer.minScale > 0 && featureLayer.minScale < scale) {
               scale = featureLayer.minScale;
            }
         }
         scales.Add(scale);
         if (scale > labelSetting.minScale) {
            labelSetting.minScale = scale;
         }
      }

      labelSetting.minScales = scales.slice(0);  //Clone array.

      //Color.
      if (labelSetting.color) {
         if (typeof labelSetting.color === 'string') {
            labelSetting.color = labelSetting.color.toLowerCase();
         }

      } else {
         labelSetting.color = "black";
      }

      //Halo color.
      if (labelSetting.haloColor) {
         if (typeof labelSetting.haloColor === 'string') {
            labelSetting.haloColor = labelSetting.haloColor.toLowerCase();
         }

      } else {
         labelSetting.haloColor = [255, 255, 255, 0.9];
      }

      //Halo size.
      if (typeof labelSetting.haloSize === "undefined") {
         labelSetting.haloSize = 1.2;
      }

      //Font family.
      if (!labelSetting.font) {
         labelSetting.font = "sans-serif";
      }

      //Font size.
      if (!labelSetting.fontSize) {
         labelSetting.fontSize = 9;
      }

      //Font weight.
      if (!labelSetting.fontWeight) {
         labelSetting.fontWeight = "bold";
      }

      //Show all labels around map edges.
      if (typeof labelSetting.showAll === "undefined") {
         labelSetting.showAll = true;
      }

      //Show all labels around map edges.
      if (typeof labelSetting.layerOnTop === "undefined") {
         labelSetting.layerOnTop = false;
      }

      //Size tolerance(s) per scale.
      if (labelSetting.sizeTols) {
         if (!Array.isArray(labelSetting.sizeTols)) {
            labelSetting.sizeTols = [labelSetting.sizeTols];
         }
      } else {
         labelSetting.sizeTols = [50];
      }

      let sizeTols: number[] = [];

      for (j = 0; j < labelSetting.minScales.length; j++) {
         if (labelSetting.sizeTols[j]) {
            sizeTols.Add(labelSetting.sizeTols[j]);
         } else {
            sizeTols.Add(labelSetting.sizeTols[labelSetting.sizeTols.length - 1]);
         }
      }

      labelSetting.sizeTols = sizeTols.slice(0);  //Clone array.

      //Label position.
      if (labelSetting.labelPosition) {
         if (featureLayer.geometryType === "polyline") {
            if (labelSetting.labelPosition !== "center-center" && labelSetting.labelPosition !== "below-center") {
               labelSetting.labelPosition = "above-center";
            }
         }

      } else { //Set to default value.
         if (!labelSetting.labelPosition) {
            if (featureLayer.geometryType === "point") {
               labelSetting.labelPosition = "above-right";

            } else if (featureLayer.geometryType === "polyline") {
               labelSetting.labelPosition = "above-center";

            } else { //polygon.
               labelSetting.labelPosition = "center-center";
            }
         }
      }

      //Set output fields.
      const outFields: string[] = [];

      for (const fieldName of labelSetting.fieldNames) {
         caseField = Util_App.getFieldNameCase(fieldName, featureLayer.fields);
         if (caseField) {
            outFields.Add(caseField);
         } else {
            if (featureLayer.fields) {
               Util.errMsg("Field name not found for label setting: " + labelSetting.layerName + " -> " + fieldName);
            } else {
               Util.errMsg("featureLayer.fields not found in REST endpoint for label setting: " + labelSetting.layerName);
            }
         }
      }

      if (outFields.length === 0) {
         continue; //No fields found that match settings.
      }

      //Add area or length field name to output fields.
      if (featureLayer.geometryType === "polyline" || featureLayer.geometryType === "polygon") {
         if (!labelSetting.sizeField) {
            if (featureLayer.geometryType === "polyline") {
               labelSetting.sizeField = "Shape__Length";

            } else { //polygon.
               labelSetting.sizeField = "Shape__Area";
            }
         }

         if (!outFields.Contains(labelSetting.sizeField)) {
            outFields.Add(labelSetting.sizeField); //Add area or length field to output fields.
         }
      }

      //Add angle field.
      if (labelSetting.pointGraphicMode) {
         if (labelSetting.angleField) {
            caseField = Util_App.getFieldNameCase(labelSetting.angleField, featureLayer.fields);
            if (caseField) {
               labelSetting.angleField = caseField; //Fix the case of the angle file name.

               if (!outFields.Contains(caseField)) {
                  outFields.Add(caseField);
               }

            } else {
               Util.logMsg("Text angle field not found for graphic text label.  Using 0 degrees for all text: " + labelSetting.layerName);
               labelSetting.angleField = "";
            }

         } else {
            Util.logMsg("Text angle field not found for graphic text label.  Using 0 degrees for all text: " + labelSetting.layerName);
            labelSetting.angleField = "";
         }
      }

      //Add font size field.
      if (labelSetting.pointGraphicMode && labelSetting.fontSizeField) {
         caseField = Util_App.getFieldNameCase(labelSetting.fontSizeField, featureLayer.fields);
         if (caseField) {
            labelSetting.fontSizeField = caseField; //Fix the case of the font size file name.

            if (!outFields.Contains(caseField)) {
               outFields.Add(caseField);
            }
         } else {
            Util.logMsg("Text font size field not found for graphic text label.  Using default value: " + labelSetting.layerName);
            labelSetting.fontSizeField = "";
         }
      }

      //Add custom processing fields.
      for (const fieldName of labelSetting.visibilityFieldNames) {
         caseField = Util_App.getFieldNameCase(fieldName, featureLayer.fields);
         if (caseField) {
            if (!outFields.Contains(caseField)) {
               outFields.Add(caseField);
            }

         } else {
            Util.errMsg("Custom field name not found for label setting: " + labelSetting.layerName + " -> " + fieldName);
         }
      }

      if (!outFields.Contains("ObjectID")) {
         outFields.Add(featureLayer.objectIdField); //Add object id to output fields.
      }
      labelSetting.outFields = outFields;
   }
   Util.logMsg("Done initializing Feature Layer Labels");
} //end Initialize label settings.

//Process all label layers.
function processLabels() {
   if (ii >= 0 && ii < Settings.labelLayers.length) {
      Util.logMsg("Done processing " + Settings.labelLayers[ii].layerName);
   }

   ii++; //Process next label setting.

   if (ii < Settings.labelLayers.length) {
      const labelSetting: Settings.iLabelLayer = Settings.labelLayers[ii];
      processLabel(labelSetting);

   } else { //All settings have been processed.
      busy = false;
   }

} // end function.

//Process label setting.
function processLabel(labelSetting: Settings.iLabelLayer) {
   if (debug) {
      Util.errMsg("Processing layer setting for: " + labelSetting.layerName);
   } else {
      Util.logMsg("Processing layer setting for: " + labelSetting.layerName);
   }

   if (!labelSetting.geometryType) {
      processLabels(); //Skip if feature layer not found.
      return;
   }

   if (MainMap.activeView.scale > labelSetting.minScale || MainMap.activeView.scale < labelSetting.maxScale) {
      processLabels(); //Skip if not within scale.
      return;
   }

   //Skip line and polygon layers if 3D.
   if (!MainMap.view2D) {
      if (labelSetting.geometryType === "polyline" || labelSetting.geometryType === "polygon") {
         processLabels();
         return;
      }
   }

   //Get input feature layer.
   const layerName: string = labelSetting.layerName;

   const featureLayer = Util_App.getOperationalLayerById(layerName) as __esri.FeatureLayer;
   if (!featureLayer || !featureLayer.visible) {
      processLabels(); //Skip to next label layer if there are issues.
      return;
   }

   //Process point feature class labels.
   if (!labelSetting.pointGraphicMode && featureLayer.geometryType === "point") {
      updatePointLabelClass(labelSetting, featureLayer);

   } else { // Line and polygon labels, point graphic text, and polygon graphic text.
      //Get output feature layer to be labeled.
      let labelsLayerName: string = labelSetting.labelsLayerName;
      if (featureLayer.geometryType !== "point" && labelSetting.renderAsGraphic) {
         labelsLayerName += "_FL";
      }
      const featOrGraphicLayer = Util_App.getOperationalLayerById(labelsLayerName);
      if (!featOrGraphicLayer) {
         Util.errMsg("Feature or graphic layer for labels not yet available for " + layerName);
         processLabels();
         return;
      }

      //Get point graphics layer if rendering as graphics.
      let pointGraphicLayer: __esri.GraphicsLayer;
      if (labelSetting.renderAsGraphic) {
         pointGraphicLayer = Util_App.getOperationalLayerById(labelSetting.labelsLayerName) as __esri.GraphicsLayer;
      }

      //Check input or output feature layers are turned off.
      if (labelSetting.renderAsGraphic) {
         if (!pointGraphicLayer.visible) {
            processLabels(); //Skip if graphics layer is turned off.
            return;
         }

      } else {
         if (!featOrGraphicLayer.visible) {
            processLabels(); //Skip if labels layer is turned off.
            return;
         }
      }

      //Make sure feature layer has required output fields.
      featureLayer.outFields = labelSetting.outFields;
      Util.logMsg("Getting layerview for: " + layerName);

      //Get feature layer view and process when ready.
      MainMap.activeView?.whenLayerView(featureLayer)?.then(function (layerView) {
         Util.logMsg("Waiting for layerview to update: " + layerName);

         if (!layerView.layer.spatialReference) {
            processLabels(); //Skip to next label layer if there are issues.
            return;
         }

         //The event below does not always fire bug!
         reactiveUtils?.whenOnce(() => !layerView.updating)?.then(() => { //Layer view is done updating.

            Util.logMsg("Layerview is done updating: " + layerName);

            //Set scale dependent font sizes.
            if (labelSetting.mapScaleChanged) {
               if (labelSetting.pointGraphicMode || labelSetting.renderAsGraphic) {
                  setScaleDependentFontSize(labelSetting.labelClass, labelSetting);

               } else {
                  const labelClass: any = featOrGraphicLayer.labelingInfo[0];
                  setScaleDependentFontSize(labelClass.symbol, labelSetting);
                  featOrGraphicLayer.labelingInfo = [labelClass];
               }
            }

            //Compute size tolerance.
            let minScale: number;
            let sizeTol: number = labelSetting.sizeTols[0];

            if (!Array.isArray(labelSetting.minScales)) {
               labelSetting.minScales = [labelSetting.minScales];
            }

            for (let i: number = labelSetting.minScales.length - 1; i >= 0; i--) {
               minScale = labelSetting.minScales[i];

               if (MainMap.activeView.scale <= minScale) {
                  sizeTol = labelSetting.sizeTols[i];
                  break;
               }
            }

            //Build query for large features where new labels may need to be added.
            let query = layerView.createQuery();
            query.outFields = labelSetting.outFields;

            labelSetting.tinySize = 0;
            let whereStr: string = null;
            let tinySizeFeet: number;

            if (featureLayer.geometryType === "polyline" || featureLayer.geometryType === "polygon") {
               if (featureLayer.geometryType === "polyline") {
                  labelSetting.tinySize = MainMap.activeView.scale ** 2 / (sizeTol * 950); // 950 Increase to show more labels.
                  tinySizeFeet = labelSetting.tinySize;

                  if (MainMap.activeView.spatialReference.isWebMercator) {
                     tinySizeFeet = tinySizeFeet / 3.281; //Convert tiny size from feet to meters.
                  }

               } else { //polygon.
                  labelSetting.tinySize = MainMap.activeView.scale ** 2 / (sizeTol * 3.5); // 3.5 Increase to show more labels.
                  tinySizeFeet = labelSetting.tinySize;

                  if (labelSetting.sizeField.Contains("acres")) {
                     tinySizeFeet = tinySizeFeet / 43560; //Convert tiny size from sq ft to acres.
                  } else if (MainMap.activeView.spatialReference.isWebMercator) {
                     tinySizeFeet = tinySizeFeet / 10.764; //Convert tiny size from sq ft to sq meters.
                  }
               }

               whereStr = labelSetting.sizeField + " >= " + tinySizeFeet;
            }

            //Util.errMsg(layerName + " Sca: " + MainMap.activeView.scale + " TinySize: " + labelSetting.tinySize.toFixed(2));

            //Add custom sql where string if available.
            const sql: string = Custom.getCustomLabelQuery(layerName);
            if (sql) {
               if (whereStr) {
                  whereStr += " and ";
               } else {
                  whereStr = "";
               }
               whereStr += sql;
            }

            //Set where.
            if (whereStr) {
               query.where = whereStr;
            }

            if (labelSetting.pointGraphicMode) {
               query.geometry = MainMap.activeView.extent; //May cause readHydratedGeometry error.
            }

            //Get all large sized features in current layer view.
            Util.logMsg("Query large features where: " + query.where);

            layerView.queryFeatures(query)?.then(function (fs) {
               Util.logMsg("# Large features: " + fs.features.length + " " + layerName);
               let labelFeatures: any[] = fs.features;

               //Clear everything if map scale changed.
               if (labelSetting.mapScaleChanged) {

                  if (labelSetting.pointGraphicMode) {
                     featOrGraphicLayer.removeAll(); //Clear all graphic text points if scale has changed.
                     labelSetting.objectIDs = [];

                  } else if (labelSetting.renderAsGraphic) {
                     labelSetting.objectIDs = [];
                     labelSetting.seqID = {};
                     labelSetting.prevSize = {};
                     labelSetting.prevText = {};
                     labelSetting.prevNumStack = {};

                     pointGraphicLayer.removeAll();  //Clear all graphic text points if scale has changed.
                  }
               }

               //Process graphic point text.
               if (labelSetting.pointGraphicMode) {
                  createAndUpdateLabels(layerName, labelSetting, featureLayer, featOrGraphicLayer, null, labelFeatures, fs);

               } else { //Process lines and polygons.
                  //Clip line and polygon results with the map extent boundary.
                  const clippedFeatures: any[] = [];

                  if (labelSetting.showAll) {
                     const extentPoly = Polygon.fromExtent(MainMap.activeView.extent);

                     const overlayFeatures: Util_App.iOverlayfeatures[] = Util_App.getClippedOverlayFeatures(fs.features, extentPoly);
                     for (const rec of overlayFeatures) {
                        clippedFeatures.Add(rec.feature); //Build array of features from the clipped polygons.
                     }
                  }

                  if (clippedFeatures.length > 0) {
                     Util.logMsg("# Clipped features: " + clippedFeatures.length);
                     labelFeatures = clippedFeatures;
                  }

                  //Get small features in current map extent that need to be blanked out.
                  query = featOrGraphicLayer.createQuery();
                  query.geometry = MainMap.activeView.extent;
                  query.where = "size < " + labelSetting.tinySize + " and labelText <> ''";

                  featOrGraphicLayer.queryFeatures(query)?.then(function (blankOutFS) {
                     Util.logMsg("# Small features to be blanked: " + blankOutFS.features.length);

                     createAndUpdateLabels(layerName, labelSetting, featureLayer, featOrGraphicLayer, pointGraphicLayer, labelFeatures, fs, blankOutFS.features);

                  })?.catch(function (error: Error) {
                     Util.errMsg("featureLayerLabels.queryFeatures failed: ", error);
                  });
               }

            })?.catch(function (error: Error) {
               Util.errMsg("layerView.queryFeatures failed: ", error);
            }); // end layerView.queryFeatures.

         }); // end updating is complete.

      })?.catch(function (error: Error) {
         Util.errMsg("LayerView is ready failed: " + layerName, error);

      }); // end layer view is ready.
   } // end is point, line, or polygon feature class.
} // end function.

//Create and update all labels.
function createAndUpdateLabels(layerName: string, labelSetting: Settings.iLabelLayer, featureLayer: any, featOrGraphicLayer: any, pointGraphicLayer: any, labelFeatures: any, fs: any, blankOutFeatures?: any) {

   //Process all features.
   const newFeatures: any[] = [];    //Labeling features to be added.
   const updateFeatures: any[] = []; //Labeling features to update the xy location.

   //Util.errMsg("Queried " + labelFeatures.length + " Big & " + blankOutFeatures.length + " Blank " + layerName);

   let lineAndPolyMode: boolean = true;
   if (labelSetting.pointGraphicMode) {
      lineAndPolyMode = false;
   }

   //Get area of current map extent.
   const extentSize: number = Math.round(MainMap.activeView.extent.height * MainMap.activeView.extent.width);

   //Process all clipped features.
   for (const feature of labelFeatures) {

      //Get the label text value(s).
      const fields: Util_App.iField[] = Util_App.getFeatureValues(fs, feature, layerName);

      //Format the label text.
      let i: number = -1;
      let labelText: string = "";
      let numStacked: number = 1;

      for (const fieldName of labelSetting.fieldNames) {
         i++;

         for (const field of fields) {
            if (field.name.toLowerCase() === fieldName.toLowerCase()) {

               if (Settings.security.accessLevel < field.accessLevel) {
                  break; //Skip locked down field names.
               }

               //Set visibility of stacked labels.
               if (labelSetting.fieldNames.length > 0) {
                  if (MainMap.activeView.scale > labelSetting.minScales[i] ||
                     MainMap.activeView.scale < labelSetting.maxScales[i]) {
                     field.value = "";
                  }
               }

               //Get custom visibility.
               const visible: boolean = Custom.getCustomLabelVisibility(field.name, field.value, feature, layerName, fs);
               if (!visible) {
                  field.value = "";
               }

               //Get custom text value.
               field.value = Custom.getCustomLabelValue(field.name, field.value, feature, layerName, fs);

               //Add new line for stacked labels.
               if (labelText !== "" && field.value !== "") {
                  numStacked++;
                  labelText += "\n";
               }

               if (field.dataType === "double" && field.value !== "") {
                  if (labelSetting.decimals && labelSetting.decimals >= 0) {
                     field.value = Util.round(field.value, labelSetting.decimals).toString();
                  }
               }

               labelText += field.value;
            }
         }
      }

      if (!labelText) {
         continue; // Skip to next feature when blank text.
      }

      //Get objectID value of the feature to be labeled.
      const objectID: number = feature.attributes[featureLayer.objectIdField];

      //Get label point or line segment geometry.
      let seqID: number = labelSetting.objectIDs.length + 1;
      let updateMode: boolean = false;
      let labelGeom: any;
      let featureSize: number = 0;
      let sizePercentChanged: number = 0;
      const geometry = feature.geometry;

      if (featureLayer.geometryType === "point") { //=========================================

         if (!labelSetting.objectIDs.Contains(objectID)) {
            const sym: any = JSON.parse(JSON.stringify(labelSetting.labelClass)); //Clone it.
            sym.text = labelText;

            sym.angle = 0;
            if (labelSetting.angleField) {
               sym.angle = Util.parseFlt(feature.attributes[labelSetting.angleField]);
               sym.angle = Custom.getCustomLabelAngle(layerName, sym.angle);
            }

            if (labelSetting.fontSizeField) {
               sym.font.size = Util.parseFlt(feature.attributes[labelSetting.fontSizeField]);
               if (sym.font.size < 6) {
                  sym.font.size = 6;
               }
               sym.font.size = computeScaleDependentFontSize(sym.font.size);
            }

            const graText = new Graphic({ geometry: feature.geometry, symbol: sym });
            featOrGraphicLayer.add(graText); //This is actually a graphics layer, not a feature layer.
         }

      } else if (featureLayer.geometryType === "polyline") { //===============================
         //Compute tiny line.
         if (MainMap.activeView.spatialReference && MainMap.activeView.spatialReference.isWebMercator) {
            featureSize = Util_App.geometryEngine.geodesicLength(geometry, "feet");
         } else {
            featureSize = Util_App.geometryEngine.planarLength(geometry, "feet");
         }

         //if (featureSize < labelSetting.tinySize) {
         //   continue; //Skip small features at extent boundary.
         //}

         //   Util.errMsg(labelText + " FeatureSize: " + featureSize.toFixed(2));

         //Compute label segment for line.
         labelGeom = getSegmentGeom(geometry, labelText, numStacked, labelSetting);

         const prevDist: number = labelSetting.prevSize[objectID];
         const prevText: boolean = labelSetting.prevText[objectID];

         if (prevDist) {
            sizePercentChanged = Math.abs(featureSize - prevDist) / prevDist * 100;

            if (featureSize < labelSetting.tinySize) {
               if (prevText) {
                  updateMode = true; //Blank out short lines.
                  labelText = "";
               } else {
                  continue; //Skip short blank lines.
               }

            } else if (!prevText || labelSetting.mapScaleChanged || sizePercentChanged > 1) { //Long line found.
               updateMode = true; //Update text and xy location of existing labels.

            } else {
               continue; // Skip if nothing changed.
            }

         } else if (featureSize < labelSetting.tinySize) { //New line found.
            continue; // Skip new short lines.
         }

         labelSetting.prevSize[objectID] = featureSize;

         if (labelText) {
            labelSetting.prevText[objectID] = true;
         } else {
            labelSetting.prevText[objectID] = false;
         }

      } else { //polygon. //===================================================================
         //Compute tiny area.
         if (MainMap.activeView.spatialReference && MainMap.activeView.spatialReference.isWebMercator) {
            featureSize = Util_App.geometryEngine.geodesicArea(geometry, "square-feet"); //square-meters
         } else {
            featureSize = Util_App.geometryEngine.planarArea(geometry, "square-feet"); //acres 43560
         }

         //Util.errMsg(labelText + " FeatureSize: " + featureSize.toFixed(2));

         //Compute label point for polygon.
         labelGeom = Util_App.getPointInsidePolygon(geometry);

         const prevArea: number = labelSetting.prevSize[objectID];
         const prevText: boolean = labelSetting.prevText[objectID];
         const prevNumStacked: number = labelSetting.prevNumStack[objectID];

         if (prevArea) {
            sizePercentChanged = Math.abs(featureSize - prevArea) / prevArea * 100;
            if (extentSize === Math.round(featureSize)) {
               sizePercentChanged = 999; //Force update when entire feature is within map extent.
            }

            if (featureSize < labelSetting.tinySize) {
               if (prevText) {
                  updateMode = true; //Blank out small areas.
                  labelText = "";
               } else {
                  continue; //Skip small blank areas.
               }

            } else if (!prevText || sizePercentChanged > 1 || numStacked !== prevNumStacked) { //Big area found.
               updateMode = true; //Update text and xy location of existing labels.

            } else {
               continue; // Skip if nothing changed.
            }

         } else if (featureSize < labelSetting.tinySize) {
            continue; // Skip new tiny areas.
         }

         labelSetting.prevSize[objectID] = featureSize;
         labelSetting.prevNumStack[objectID] = numStacked;

         if (labelText) {
            labelSetting.prevText[objectID] = true;
         } else {
            labelSetting.prevText[objectID] = false;
         }
      } //end if polygon. =================================================

      //Update object id list.
      if (!labelSetting.objectIDs.Contains(objectID)) {
         labelSetting.objectIDs.Add(objectID);
      }

      if (lineAndPolyMode) {
         //Create graphic to label.
         if (updateMode) {
            seqID = labelSetting.seqID[objectID];
         }

         const gra: any = new Graphic({
            geometry: labelGeom,
            attributes: {
               objectID: seqID,
               real_oid: objectID,
               labelText: labelText,
               size: featureSize,
            },
         });

         if (updateMode) {
            updateFeatures.Add(gra);

         } else {
            labelSetting.seqID[objectID] = seqID;
            newFeatures.Add(gra);
         }

         //Render as graphic.
         if (labelSetting.renderAsGraphic) {

            if (updateMode) {
               for (const gra2 of pointGraphicLayer.graphics.items) {
                  if (objectID === gra2.attributes.real_oid) {
                     gra2.geometry = labelGeom;
                     gra2.symbol.text = labelText;

                     if (featureLayer.geometryType === "polyline") {
                        gra2.symbol.angle = segmentAngle;
                        gra2.geometry = midPoint;
                     }
                  }
               }

            } else {
               //Use label class for lack of a better variable.
               const sym2: any = JSON.parse(JSON.stringify(labelSetting.labelClass)); //Clone it.
               let geom: any = labelGeom;

               sym2.text = labelText;

               if (labelSetting.fontSizeField) {
                  sym2.font.size = Util.parseFlt(feature.attributes[labelSetting.fontSizeField]);
                  if (sym2.font.size < 6) {
                     sym2.font.size = 6;
                  }
                  sym2.font.size = computeScaleDependentFontSize(sym2.font.size);
               }

               if (featureLayer.geometryType === "polyline") {
                  sym2.angle = segmentAngle;
                  sym2.verticalAlignment = "middle";
                  sym2.xoffset = 0;
                  sym2.yoffset = 0;
                  geom = midPoint;
               }

               const graText = new Graphic({
                  geometry: geom,
                  symbol: sym2,
                  attributes: {
                     real_oid: objectID,
                  },
               });

               pointGraphicLayer.add(graText);
            }
         } //end render as graphic.
      } //end line and poly mode.
   } //end for all features.

   Util.logMsg("Done computing labels: New & Update: " + newFeatures.length + " " + updateFeatures.length);

   //Process all blank out features.
   if (lineAndPolyMode) {
      for (const feature of blankOutFeatures) {
         const gra: any = new Graphic({
            geometry: feature.geometry,
            attributes: {
               objectID: feature.attributes.objectID, //Set sequential id.  This is not the oid.
               real_oid: feature.attributes.real_oid, //Real objectID.
               labelText: "",
               size: feature.attributes.size,
            },
         });

         updateFeatures.Add(gra);

         labelSetting.prevText[feature.attributes.real_oid] = false;

         //Blank out graphics for render as graphics mode.
         if (labelSetting.renderAsGraphic) {
            for (const gra3 of pointGraphicLayer.graphics.items) {
               if (gra3.attributes.real_oid === feature.attributes.real_oid) {
                  gra3.symbol.text = "";
                  break;
               }
            }
         }

         //if (labelFeatures.length < 10) {
         //   Util.errMsg(layerName + " After blank updates added: " + updateFeatures.length);
         //}
      }
   }

   //Refresh the display.
   //Util.errMsg("New features before", newFeatures);
   //Util.errMsg("Update features", updateFeatures);
   labelSetting.mapScaleChanged = false;

   if (newFeatures.length > 0 || updateFeatures.length > 0) {
      Util.logMsg("Apply edits: New & Update: " + newFeatures.length + " " + updateFeatures.length);

      featOrGraphicLayer.applyEdits({ updateFeatures: updateFeatures, addFeatures: newFeatures })?.then(function (results) {
         Util.logMsg("Done apply edits: " + layerName);

         if (results.updateFeatureResults.length > 0) {
            if (results.updateFeatureResults[0].error) {
               Util.errMsg(results.updateFeatureResults[0].error);
            }
         }
         if (results.addFeatureResults.length > 0) {
            if (results.addFeatureResults[0].error) {
               Util.errMsg(results.addFeatureResults[0].error);
            }
         }

         processLabels();
      });

   } else {
      processLabels();
   }

} //end function.

//Create and update all labels for point feature class.
function updatePointLabelClass(labelSetting: Settings.iLabelLayer, featureLayer: any) {
   let i: number = -1;
   let expression: string = "";
   const layerName: string = labelSetting.layerName;
   const labelClass = labelSetting.labelClass;

   for (const fieldName of labelSetting.fieldNames) {
      i++;

      //Skip fields that are not within scale range..
      if (MainMap.activeView.scale > labelSetting.minScales[i] ||
         MainMap.activeView.scale < labelSetting.maxScales[i]) {
         continue;
      }

      //Get custom visibility.
      const visible: boolean = Custom.getCustomLabelFieldNameVisibility(layerName, fieldName);
      if (!visible) {
         continue;
      }

      //Add new line for stacked labels.
      if (expression !== "") {
         expression += " + TextFormatting.NewLine + ";
      }
      expression += "$feature." + fieldName;
   }

   //Override label expression with custom if available.
   const customExpression: string = Custom.getCustomLabelFormatExpression(layerName);
   if (customExpression) {
      labelClass.labelExpressionInfo.expression = customExpression;
   } else {
      labelClass.labelExpressionInfo.expression = expression;
   }

   //Get custom sql where string.
   const sql: string = Custom.getCustomLabelQuery(layerName);
   if (sql) {
      labelClass.where = sql;
   }

   //Set font size by map scale.
   setScaleDependentFontSize(labelClass.symbol, labelSetting);

   //Set the label class.
   featureLayer.labelingInfo = [labelClass];
   processLabels();
}

//Get line segment for labeling lines.
function getSegmentGeom(lineGeom: any, labelText: string, numStacked: number, labelSetting: Settings.iLabelLayer): any {
   const rad90: number = 1.5708;
   const rad180: number = 3.14159;

   let i: number;
   let j: number;
   let pnt1: any;
   let pnt2: any;
   let angleRad: number;
   let extendDist: number;

   //Abort if corupt geometry.
   if (!lineGeom || !lineGeom.paths || lineGeom.paths.length === 0 || lineGeom.paths[0].length === 0) {
      Util.errMsg("Geometry is missing for: " + labelSetting.layerName + " " + labelText);
      return lineGeom;
   }

   //Process a two point line.
   if (lineGeom.paths.length === 1 && lineGeom.paths[0].length === 2) {
      pnt1 = lineGeom.getPoint(0, 0);
      pnt2 = lineGeom.getPoint(0, 1);
      angleRad = Math.atan2((pnt2.y - pnt1.y), (pnt2.x - pnt1.x));
      midPoint = new Point({ x: (pnt1.x + pnt2.x) / 2, y: (pnt1.y + pnt2.y) / 2, spatialReference: MainMap.activeView.spatialReference });

   } else {
      //Delete all but longest path.
      if (lineGeom.paths.length > 1) {
         let maxPnt: number = 0;

         for (i = 0; i < lineGeom.paths.length; i++) {
            const numPnt: number = lineGeom.paths[i].length;
            if (numPnt > maxPnt) {
               maxPnt = numPnt;
               j = i; //Save index to keeper ringer.
            }
         }

         for (i = lineGeom.paths.length - 1; i >= 0; i--) {
            if (i !== j) {
               lineGeom.removePath(i);
            }
         }
      }

      //Compute total and half distance of line.
      let totalDist: number;
      if (MainMap.activeView.spatialReference.isWebMercator) {
         totalDist = Util_App.geometryEngine.geodesicLength(lineGeom);
      } else {
         totalDist = Util_App.geometryEngine.planarLength(lineGeom);
      }

      const halfDist: number = totalDist / 2;

      //Find the middle of the line.
      let curDist: number = 0;
      let prevDist: number = 0;
      let prevPnt: any;
      let found: boolean = false;

      for (i = 0; i < lineGeom.paths.length; i++) {
         if (found) {
            break;
         }

         for (j = 0; j < lineGeom.paths[i].length; j++) {
            const pnt: any = lineGeom.getPoint(i, j);

            if (i > 0 || j > 0) {
               curDist += pnt.distance(prevPnt);
               if (curDist >= halfDist) {
                  found = true;
                  pnt1 = prevPnt;
                  pnt2 = pnt;
                  break;
               }
            }
            prevPnt = pnt;
            prevDist = curDist;
         }
      }

      extendDist = halfDist - prevDist;

      //Compute angle.
      angleRad = Math.atan2((pnt2.y - pnt1.y), (pnt2.x - pnt1.x));

      //Compute midpoint.
      const midX: number = pnt1.x + (extendDist * Math.cos(angleRad));
      const midY: number = pnt1.y + (extendDist * Math.sin(angleRad));
      midPoint = new Point({ x: midX, y: midY, spatialReference: MainMap.activeView.spatialReference });
   }

   //Create circular buffer with geometry engine.
   let numChar: number = 2; //Minimum number of characters to determine segment length.
   if (labelText.length > numChar) {
      numChar = labelText.length;
   }

   extendDist = numChar * MainMap.activeView.scale / 95; // 95;

   let bufGeom: any;
   if (MainMap.activeView.spatialReference.isWebMercator) {
      bufGeom = Util_App.geometryEngine.geodesicBuffer(midPoint, extendDist, "feet");
   } else {
      bufGeom = Util_App.geometryEngine.buffer(midPoint, extendDist, "feet");
   }

   //Clip with extent of line.
   const segmentGeom: any = Util_App.geometryEngine.clip(lineGeom, bufGeom.extent);
   if (!segmentGeom) {
      return lineGeom;
   }

   //Move the line up or down a bit.
   if (labelSetting.labelPosition !== "center-center") {
      extendDist = labelSetting.activeFontSize * numStacked * MainMap.activeView.resolution;

      if (labelSetting.aboveAndBelow) {
         extendDist *= 1.3; //Give the labels some more room if labeling above and below the line.
      }

      if ((angleRad > rad90 && angleRad < rad180) || (angleRad < -rad90 && angleRad > -rad180)) {
         extendDist = -extendDist;
      }

      if (labelSetting.labelPosition === "below-center") {
         extendDist = -extendDist;
      }

      for (i = 0; i < segmentGeom.paths.length; i++) {
         for (j = 0; j < segmentGeom.paths[i].length; j++) {
            const pnt: any = segmentGeom.getPoint(i, j);

            const x: number = pnt.x + (extendDist * Math.cos(angleRad + rad90));
            const y: number = pnt.y + (extendDist * Math.sin(angleRad + rad90));
            const offsetPnt: any = new Point({ x: x, y: y, spatialReference: MainMap.activeView.spatialReference });

            segmentGeom.setPoint(i, j, offsetPnt);
         }
      }

      //Adjust offset for graphic text.
      midPoint.x += extendDist * Math.cos(angleRad + rad90);
      midPoint.y += extendDist * Math.sin(angleRad + rad90);
   }

   //Compute angle of segment for graphic text.
   segmentAngle = -angleRad * (180 / Math.PI);
   if ((angleRad > rad90 && angleRad < rad180) || (angleRad < -rad90 && angleRad > -rad180)) {
      segmentAngle += 180;
   }

   if (segmentAngle < 0) {
      segmentAngle += 360;
   }

   return segmentGeom;
}

//Set scale dependent font size.
function setScaleDependentFontSize(sym: any, labelSetting: Settings.iLabelLayer) {

   if (MainMap.view2D && labelSetting.mapScaleChanged) {
      let fontSize: number = labelSetting.fontSize;

      fontSize = computeScaleDependentFontSize(fontSize);
      if (MainMap.view2D || labelSetting.pointGraphicMode) {
         if (sym.font) {
            sym.font.size = fontSize;
         }
      } else {
         sym.symbolLayers.getItemAt(0).size = fontSize; //Currently not supported.
      }
      labelSetting.activeFontSize = fontSize;
   }
}

//Compute scale dependent font size.
function computeScaleDependentFontSize(fontSize: any, begScale: number = 2001): number {
   let outFontSize: number = fontSize;

   if (MainMap.activeView.scale < begScale) {
      let sizeToAdd: number = ((begScale - MainMap.activeView.scale) ** 2.1) / 1000000;
      sizeToAdd = Util.parseFlt(sizeToAdd.toFixed(1));
      outFontSize += sizeToAdd;
      //Util.errMsg("*** Dependent scale sizeToAdd Font: " + MainMap.activeView.scale.toFixed(2) + " " + sizeToAdd.toFixed(2) + " " + fontSize);
   }
   return outFontSize;
}

//Create empty point feature layer for labeling polygons and pass back labelClass for points.
function createPointFeatureLayer(labelSetting: Settings.iLabelLayer): any {

   const fields: any = [
      new Field({ name: "labelText", alias: "labelText", type: "string" }),
      new Field({ name: "objectID", alias: "objectID", type: "oid" }),
      new Field({ name: "real_oid", alias: "real_oid", type: "integer" }),
      new Field({ name: "size", alias: "size", type: "double" }),
   ];

   //Default label symbology. family: "Avenir Next LT Pro Medium",
   const sym = getTextSymbol(labelSetting);

   if (labelSetting.renderAsGraphic) {
      // Hide labels when rendering as graphics.
      if (MainMap.view2D) {
         sym.font.size = 0;
      } else {
         sym.symbolLayers[0].size = 0;
      }
   }

   const labelClass = new LabelClass({
      labelExpressionInfo: { expression: "$feature.labelText" },
      labelPlacement: labelSetting.labelPosition,
      //deconflictionStrategy: "none",
      symbol: sym,
   });

   let id: string = labelSetting.labelsLayerName;
   let listMode: any = "show";

   //Hide the feature layer if graphics are being used instead of labels.
   if (labelSetting.renderAsGraphic) {
      id = labelSetting.labelsLayerName + "_FL";
      listMode = "hide";
   }

   let pointRenderer = new SimpleRenderer({
      symbol: new SimpleMarkerSymbol({
         color: new Color([240, 0, 0, 0.01]), // [240, 0, 0, 1], Use this opacity when testing.
         size: 0.01,
      })
   });

   const fl = new FeatureLayer({
      id: id,
      title: id,
      geometryType: "point",
      objectIdField: "objectID",
      fields: fields,
      outFields: ["*"],
      spatialReference: MainMap.activeView.spatialReference,
      legendEnabled: false,
      listMode: listMode,
      maxScale: labelSetting.maxScale,
      minScale: labelSetting.minScale,
      popupEnabled: false,
      renderer: pointRenderer,
      labelingInfo: [labelClass],
      source: [],
   });

   //Add feature layer to map.
   addLayerToMap(labelSetting, fl);
}

//Create empty line feature layer for labeling lines.
function createLineFeatureLayer(labelSetting: Settings.iLabelLayer) {

   const fields: any = [
      new Field({ name: "labelText", alias: "labelText", type: "string" }),
      new Field({ name: "objectID", alias: "objectID", type: "oid" }),
      new Field({ name: "real_oid", alias: "real_oid", type: "integer" }),
      new Field({ name: "size", alias: "size", type: "double" }),
   ];

   //Default label symbology.
   const sym = getTextSymbol(labelSetting);

   if (labelSetting.renderAsGraphic) {
      if (sym.font) {
         sym.font.size = 0; // Hide labels when rendering as graphics.

      } else if (sym.symbolLayers[0]) {
         sym.symbolLayers[0].size = 0;
      }
   }

   const labelClass = new LabelClass({
      labelExpressionInfo: { expression: "$feature.labelText" },
      labelPlacement: "center-along",
      //deconflictionStrategy: "none",
      symbol: sym,
   });

   let id: string = labelSetting.labelsLayerName;
   let listMode: any = "show";

   //Hide the feature layer if graphics are being used instead of labels.
   if (labelSetting.renderAsGraphic) {
      id = labelSetting.labelsLayerName + "_FL";
      listMode = "hide";
   }

   let lineRenderer = new SimpleRenderer({
      symbol: new SimpleLineSymbol({
         color: new Color([240, 0, 0, 0.01]), // [240, 0, 0, 1], Use this opacity when testing.
         width: 0.01,
      })
   });

   const fl = new FeatureLayer({
      id: id,
      title: id,
      geometryType: "polyline",
      objectIdField: "objectID",
      fields: fields,
      outFields: ["*"],
      spatialReference: MainMap.activeView.spatialReference,
      legendEnabled: false,
      listMode: listMode,
      maxScale: labelSetting.maxScale,
      minScale: labelSetting.minScale,
      popupEnabled: false,
      renderer: lineRenderer,
      labelingInfo: [labelClass],
      source: [],
   });

   //Add feature layer to map.
   addLayerToMap(labelSetting, fl);
}

//Create label class for labeling point feature layers. (Not used for polygons)
function createLabelClassForPoints(labelSetting: Settings.iLabelLayer): any {

   //Default label symbology.
   const sym = getTextSymbol(labelSetting);

   const labelClass = new LabelClass({
      labelExpressionInfo: { expression: "$feature.OBJECTID + TextFormatting.NewLine + $feature.SCALE" },
      labelPlacement: labelSetting.labelPosition,
      //deconflictionStrategy: "none",
      symbol: sym,
   });

   labelSetting.labelClass = labelClass;
}

//Create empty graphics layer for labeling point feature layers that have an angle with text point graphics.
function createGraphicsLayerForTextPoints(labelSetting: Settings.iLabelLayer): any {
   const sym = getTextSymbol(labelSetting, true);
   labelSetting.labelClass = sym;

   if (MainMap.view2D) {
      if (labelSetting.labelPosition.StartsWith("above")) {
         labelSetting.labelClass.verticalAlignment = "bottom";
         labelSetting.labelClass.yoffset = labelSetting.labelClass.font.size / 1.5;

      } else if (labelSetting.labelPosition.StartsWith("below")) {
         labelSetting.labelClass.verticalAlignment = "top";
         labelSetting.labelClass.yoffset = -labelSetting.labelClass.font.size / 1.5;

      } else {
         labelSetting.labelClass.verticalAlignment = "middle";
      }

      if (labelSetting.labelPosition.EndsWith("left")) {
         labelSetting.labelClass.horizontalAlignment = "right";
         labelSetting.labelClass.xoffset = -labelSetting.labelClass.font.size / 1.5;

      } else if (labelSetting.labelPosition.EndsWith("right")) {
         labelSetting.labelClass.horizontalAlignment = "left";
         labelSetting.labelClass.xoffset = labelSetting.labelClass.font.size / 1.5;

      } else {
         labelSetting.labelClass.horizontalAlignment = "center";
      }
   }

   //Disable alignment for multi-line point graphics because of offset issues.
   if (labelSetting.fieldNames.length > 1) {
      if (labelSetting.angleField) {
         labelSetting.labelClass.verticalAlignment = null;
         labelSetting.labelClass.horizontalAlignment = null;
      }
   }

   const gl = new GraphicsLayer({
      id: labelSetting.labelsLayerName,
      title: labelSetting.labelsLayerName,
      listMode: "show",
      maxScale: labelSetting.maxScale,
      minScale: labelSetting.minScale,
   });

   //Add feature layer to map.
   addLayerToMap(labelSetting, gl);
}

//Get text symbol.
function getTextSymbol(labelSetting: Settings.iLabelLayer, force2D: boolean = false) {
   let sym: any;

   if (MainMap.view2D || force2D) {
      sym = {
         type: "text",  // autocasts as new Text Symbol.
         color: labelSetting.color,
         font: {
            family: labelSetting.font.toLowerCase(),
            size: labelSetting.fontSize,
            weight: labelSetting.fontWeight.toLowerCase(),
         },
         haloColor: labelSetting.haloColor,
         haloSize: labelSetting.haloSize,
      };

   } else {
      sym = {
         type: "label-3d",
         symbolLayers: [{
            type: "text",  // autocasts as new TextSymbol3DLayer()
            material: { color: labelSetting.color },
            size: labelSetting.fontSize,
            halo: { color: labelSetting.haloColor, size: labelSetting.haloSize },
            font: {
               family: labelSetting.font.toLowerCase(),
               weight: labelSetting.fontWeight.toLowerCase(),
            },
         }],
      };
   }
   return sym;
}

//Add new labels layer to main map.
function addLayerToMap(labelSetting: Settings.iLabelLayer, fl: any) {
   let jj: number = -1;
   let i: number;
   let layers: any;

   if (labelSetting.layerOnTop) {

      //Get the "Labels" group layer.
      for (i = 0; i < MainMap.map.layers.length; i++) {
         const layerLabels: any = MainMap.map.layers.getItemAt(i);
         if (layerLabels.id === "Labels" && layerLabels.type === "group") {
            layerLabels.visible = true;
            layers = layerLabels.layers;
            break;
         }
      }

      if (layers) {
         layers.add(fl); //Labels group layer exists, so add the labels to the group.
      } else {
         //CUSTOM! Poway
         //MainMap.map.layers.add(fl);

         //Add layer to top of source layer's group
         for (i = 0; i < MainMap.map.layers.length; i++) {
            const layer1: any = MainMap.map.layers.getItemAt(i);

            if (layer1.type === "group") {
               for (let j: number = 0; j < layer1.layers.length; j++) {
                  const layer2: any = layer1.layers.getItemAt(j);
                  if (layer2.id === labelSetting.layerName) {
                     jj = j;
                     layers = layer1.layers;
                     break;
                  }
               }

            } else if (layer1.id === labelSetting.layerName) {
               jj = i;
               layers = MainMap.map.layers;
               break;
            }

            if (jj !== -1) {
               break;
            }
         }

         //fl.visible = false;
         layers.add(fl);
      }

   } else {
      for (i = 0; i < MainMap.map.layers.length; i++) {
         const layer1: any = MainMap.map.layers.getItemAt(i);

         if (layer1.type === "group") {
            for (let j: number = 0; j < layer1.layers.length; j++) {
               const layer2: any = layer1.layers.getItemAt(j);
               if (layer2.id === labelSetting.layerName) {
                  jj = j;
                  layers = layer1.layers;
                  break;
               }
            }

         } else if (layer1.id === labelSetting.layerName) {
            jj = i;
            layers = MainMap.map.layers;
            break;
         }

         if (jj !== -1) {
            break;
         }
      }

      //fl.visible = false;
      layers.add(fl, jj + 1);
   }
}
